import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { m as motion } from "framer-motion";
import { projects as localProjects, Project } from "@/data/projects";
import { ProjectCard } from "./ProjectCard";
import { PortfolioFilter } from "./PortfolioFilter";
import { supabase } from "@/integrations/supabase/client";
import { useCategories } from "@/hooks/useCategories";

interface PortfolioGridProps {
  showFilter?: boolean;
  limit?: number;
  initialCategory?: string;
  featuredOnly?: boolean;
}

export function PortfolioGrid({
  showFilter = true,
  limit,
  initialCategory = "All",
  featuredOnly = false,
}: PortfolioGridProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get("category");
  const { categories } = useCategories();
  
  // Check if URL category is valid
  const getInitialCategory = (): string => {
    if (categoryFromUrl) {
      const validCategoryNames = categories.map(c => c.name);
      if (validCategoryNames.includes(categoryFromUrl)) {
        return categoryFromUrl;
      }
    }
    return initialCategory;
  };

  const [activeCategory, setActiveCategory] = useState<string>(
    getInitialCategory()
  );
  const [dbProjects, setDbProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Sync URL with category changes
  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    if (category === "All") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", category);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Sync state when URL changes (e.g., from footer link)
  useEffect(() => {
    const newCategory = getInitialCategory();
    if (newCategory !== activeCategory) {
      setActiveCategory(newCategory);
    }
  }, [categoryFromUrl, categories]);

  useEffect(() => {
    const fetchProjects = async () => {
      // Cards only need these fields. Skipping long_description and the code
      // snippets (about 180 KB of the table) keeps the home and portfolio pages light.
      const [{ data, error }, { data: withCode }] = await Promise.all([
        supabase
          .from("projects")
          .select(
            "id, title, slug, category, client, year, technologies, role, description, thumbnail, thumbnail_width, thumbnail_height, images, demo_url, demo_type, code_url, youtube_url, code_snippet_language, featured, hidden"
          )
          .order("created_at", { ascending: false }),
        supabase.from("projects").select("id").not("code_snippet_code", "is", null).neq("code_snippet_code", ""),
      ]);
      const codeIds = new Set((withCode || []).map((r) => r.id));

      if (data && data.length > 0) {
        // Transform database format to match our Project type
        const transformedProjects: Project[] = data
          .filter((p) => !p.hidden) // Filter out hidden projects
          .map((p) => ({
            id: p.id,
            title: p.title,
            slug: p.slug,
            category: p.category,
            client: p.client,
            year: p.year,
            technologies: p.technologies || [],
            role: p.role,
            description: p.description,
            longDescription: "",
            thumbnail: p.thumbnail,
            thumbnailWidth: p.thumbnail_width ?? undefined,
            thumbnailHeight: p.thumbnail_height ?? undefined,
            images: p.images || [],
            demoUrl: p.demo_url || undefined,
            demoType: (p.demo_type as Project["demoType"]) || undefined,
            codeUrl: p.code_url || undefined,
            youtubeUrl: p.youtube_url || undefined,
            hasCodeSnippet: codeIds.has(p.id),
            featured: p.featured,
          }));
        setDbProjects(transformedProjects);
      }
      setIsLoading(false);
    };

    fetchProjects();
  }, []);

  // Use database projects if available, otherwise fall back to local data
  const allProjects = dbProjects.length > 0 ? dbProjects : localProjects;

  const filteredProjects = useMemo(() => {
    let filtered = allProjects;

    // Filter by featured if requested
    if (featuredOnly) {
      filtered = filtered.filter((p) => p.featured);
    }

    // Filter by category - support comma-separated categories
    if (activeCategory !== "All") {
      filtered = filtered.filter((p) => {
        const projectCategories = p.category.split(",").map((c) => c.trim());
        return projectCategories.includes(activeCategory);
      });
    }

    // Apply limit
    if (limit) {
      filtered = filtered.slice(0, limit);
    }

    return filtered;
  }, [activeCategory, limit, allProjects, featuredOnly]);

  if (isLoading) {
    return (
      <div className="space-y-8 md:space-y-12">
        {showFilter && (
          <PortfolioFilter
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="aspect-video rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 md:space-y-12">
      {showFilter && (
        <PortfolioFilter
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
        />
      )}

      <div className="columns-1 md:columns-2 lg:columns-3 gap-6 md:gap-8">
        {filteredProjects.map((project, index) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.3, 
              delay: index * 0.02,
              ease: "easeOut"
            }}
            className="break-inside-avoid mb-6 md:mb-8"
          >
            <ProjectCard project={project} index={index} priority={index < 3} />
          </motion.div>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            No portfolio items found in this category.
          </p>
        </div>
      )}
    </div>
  );
}
